import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { toast } from 'sonner';
import { 
  LayoutDashboard, Package, FileText, Users, MessageSquare, 
  DollarSign, Settings, LogOut, Plus, Edit, Trash2, Eye,
  TrendingUp, ShoppingCart, Clock, CheckCircle, XCircle,
  Upload, Save, X, RefreshCw, Globe
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const isProduction = window.location.hostname.includes('spoolfix') || window.location.hostname.includes('emergent.host');
const API = isProduction ? "/api" : `${BACKEND_URL}/api`;

// Admin Context
const TeamAdminContext = createContext();

export const useTeamAdmin = () => useContext(TeamAdminContext);

export const TeamAdminProvider = ({ children }) => {
  const [adminToken, setAdminToken] = useState(localStorage.getItem("teamAdminToken"));
  const [adminUser, setAdminUser] = useState(localStorage.getItem("teamAdminUser"));
  const [isTeamAdmin, setIsTeamAdmin] = useState(!!localStorage.getItem("teamAdminToken"));

  const teamAdminLogin = async (username, password) => {
    const res = await axios.post(`${API}/admin/team-login`, { username, password });
    localStorage.setItem("teamAdminToken", res.data.access_token);
    localStorage.setItem("teamAdminUser", res.data.username);
    setAdminToken(res.data.access_token);
    setAdminUser(res.data.username);
    setIsTeamAdmin(true);
    return res.data;
  };

  const teamAdminLogout = () => {
    localStorage.removeItem("teamAdminToken");
    localStorage.removeItem("teamAdminUser");
    setAdminToken(null);
    setAdminUser(null);
    setIsTeamAdmin(false);
  };

  const getTeamAdminHeaders = () => ({
    Authorization: `Bearer ${adminToken}`
  });

  return (
    <TeamAdminContext.Provider value={{ isTeamAdmin, adminToken, adminUser, teamAdminLogin, teamAdminLogout, getTeamAdminHeaders }}>
      {children}
    </TeamAdminContext.Provider>
  );
};

// Admin Login Page
export const TeamAdminLoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const { teamAdminLogin, isTeamAdmin } = useTeamAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    if (isTeamAdmin) {
      navigate('/team-admin');
    }
  }, [isTeamAdmin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    try {
      console.log('Attempting login with:', username);
      console.log('API URL:', process.env.REACT_APP_BACKEND_URL);
      await teamAdminLogin(username, password);
      toast.success('Login successful!');
      navigate('/team-admin');
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      const msg = error.response?.data?.detail || error.message || 'Login failed';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-12 px-4">
      <Card className="w-full max-w-md bg-slate-800/50 border-slate-700">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-sky-500/20 rounded-full flex items-center justify-center mb-4">
            <Settings className="w-8 h-8 text-sky-400" />
          </div>
          <CardTitle className="text-2xl text-white">Spoolfix Team Login</CardTitle>
          <CardDescription className="text-slate-400">Enter your credentials to access admin panel</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-slate-300">Username</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white"
                placeholder="Enter username"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white"
                placeholder="Enter password"
                required
              />
            </div>
            {errorMsg && (
              <div className="p-3 bg-red-500/20 border border-red-500/50 rounded text-red-400 text-sm">
                {errorMsg}
              </div>
            )}
            <Button type="submit" className="w-full bg-sky-500 hover:bg-sky-600" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Dashboard
export const TeamAdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { teamAdminLogout, getTeamAdminHeaders, adminUser, isTeamAdmin } = useTeamAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isTeamAdmin) {
      navigate('/team-admin/login');
      return;
    }
    fetchDashboard();
  }, [isTeamAdmin, navigate]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard`, { headers: getTeamAdminHeaders() });
      setDashboardData(res.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
      if (error.response?.status === 401) {
        teamAdminLogout();
        navigate('/team-admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    teamAdminLogout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const menuItems = [
    { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'quotes', label: 'Quotes', icon: FileText },
    { id: 'orders', label: 'Orders', icon: ShoppingCart },
    { id: 'customers', label: 'Customers', icon: Users },
    { id: 'materials', label: 'Materials', icon: Package },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'contacts', label: 'Messages', icon: MessageSquare },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Sidebar */}
      <div className="w-64 bg-slate-800 border-r border-slate-700 flex flex-col">
        <div className="p-4 border-b border-slate-700">
          <h1 className="text-xl font-bold text-white">SpoolFix Admin</h1>
          <p className="text-sm text-slate-400">Welcome, {adminUser}</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                activeTab === item.id 
                  ? 'bg-sky-500/20 text-sky-400' 
                  : 'text-slate-400 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-700">
          <Button onClick={handleLogout} variant="outline" className="w-full border-slate-600 text-slate-300 hover:bg-slate-700">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {activeTab === 'overview' && <OverviewTab data={dashboardData} onRefresh={fetchDashboard} />}
          {activeTab === 'quotes' && <QuotesTab headers={getTeamAdminHeaders()} />}
          {activeTab === 'orders' && <OrdersTab headers={getTeamAdminHeaders()} />}
          {activeTab === 'customers' && <CustomersTab headers={getTeamAdminHeaders()} />}
          {activeTab === 'materials' && <MaterialsTab headers={getTeamAdminHeaders()} />}
          {activeTab === 'pricing' && <PricingTab headers={getTeamAdminHeaders()} />}
          {activeTab === 'contacts' && <ContactsTab headers={getTeamAdminHeaders()} />}
        </div>
      </div>
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ data, onRefresh }) => {
  if (!data) return null;
  const { statistics, recent } = data;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Dashboard Overview</h2>
        <Button onClick={onRefresh} variant="outline" size="sm" className="border-slate-600 text-slate-300">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard title="Total Quotes" value={statistics.quotes.total} subValue={`${statistics.quotes.today} today`} icon={FileText} color="sky" />
        <StatCard title="Total Orders" value={statistics.orders.total} subValue={`${statistics.orders.pending} pending`} icon={ShoppingCart} color="green" />
        <StatCard title="Customers" value={statistics.users.total} subValue={`${statistics.users.today} today`} icon={Users} color="purple" />
        <StatCard title="Site Visitors" value={statistics.visitors?.total || 0} subValue={`${statistics.visitors?.today || 0} today (${statistics.visitors?.unique_today || 0} unique)`} icon={Globe} color="orange" />
        <StatCard title="Conversion Rate" value={`${statistics.conversion_rate}%`} subValue="quotes to orders" icon={TrendingUp} color="yellow" />
      </div>

      {/* Order Status */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Order Status Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatusBadge label="Pending" value={statistics.orders.pending} color="yellow" />
            <StatusBadge label="In Production" value={statistics.orders.in_production} color="blue" />
            <StatusBadge label="Completed" value={statistics.orders.completed} color="green" />
            <StatusBadge label="New Messages" value={statistics.contacts.new} color="red" />
            <StatusBadge label="Materials" value={statistics.materials.total} color="purple" />
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-lg">Recent Quotes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recent.quotes.slice(0, 5).map((quote) => (
                <div key={quote.id} className="flex justify-between items-center p-2 bg-slate-700/30 rounded">
                  <div>
                    <p className="text-white text-sm font-medium">{quote.contact_name}</p>
                    <p className="text-slate-400 text-xs">{quote.id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sky-400 font-medium">${quote.total?.toFixed(2)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${quote.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'}`}>
                      {quote.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-lg">Recent Customers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recent.users.slice(0, 5).map((user) => (
                <div key={user.id} className="flex justify-between items-center p-2 bg-slate-700/30 rounded">
                  <div>
                    <p className="text-white text-sm font-medium">{user.company_name}</p>
                    <p className="text-slate-400 text-xs">{user.email}</p>
                  </div>
                  <p className="text-slate-400 text-xs">{user.contact_person}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, subValue, icon: Icon, color }) => {
  const colors = {
    sky: 'bg-sky-500/20 text-sky-400',
    green: 'bg-green-500/20 text-green-400',
    purple: 'bg-purple-500/20 text-purple-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    orange: 'bg-orange-500/20 text-orange-400',
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-400 text-sm">{title}</p>
            <p className="text-2xl font-bold text-white">{value}</p>
            <p className="text-slate-500 text-xs">{subValue}</p>
          </div>
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colors[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const StatusBadge = ({ label, value, color }) => {
  const colors = {
    yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
    red: 'bg-red-500/20 text-red-400 border-red-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  };

  return (
    <div className={`p-3 rounded-lg border ${colors[color]} text-center`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs">{label}</p>
    </div>
  );
};

// Quotes Tab
const QuotesTab = ({ headers }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const res = await axios.get(`${API}/admin/quotes`, { headers });
      setQuotes(res.data);
    } catch (error) {
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/quotes/${id}`, { status }, { headers });
      toast.success('Quote updated');
      fetchQuotes();
    } catch (error) {
      toast.error('Failed to update quote');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-white">Quote Requests ({quotes.length})</h2>
      <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-700/50">
            <tr>
              <th className="text-left p-3 text-slate-300">Quote ID</th>
              <th className="text-left p-3 text-slate-300">Customer</th>
              <th className="text-left p-3 text-slate-300">Company</th>
              <th className="text-left p-3 text-slate-300">Port</th>
              <th className="text-right p-3 text-slate-300">Total</th>
              <th className="text-center p-3 text-slate-300">Files</th>
              <th className="text-center p-3 text-slate-300">Status</th>
              <th className="text-center p-3 text-slate-300">Actions</th>
            </tr>
          </thead>
          <tbody>
            {quotes.map((quote) => (
              <tr key={quote.id} className="border-t border-slate-700 hover:bg-slate-700/30">
                <td className="p-3 text-white font-mono text-sm">{quote.id}</td>
                <td className="p-3 text-white">{quote.contact_name}</td>
                <td className="p-3 text-slate-400">{quote.company_name || '-'}</td>
                <td className="p-3 text-slate-400">{quote.delivery_port}</td>
                <td className="p-3 text-sky-400 text-right font-medium">${quote.total?.toFixed(2)}</td>
                <td className="p-3 text-center">
                  {quote.attachments && quote.attachments.length > 0 ? (
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                      {quote.attachments.length} file(s)
                    </span>
                  ) : (
                    <span className="text-slate-500 text-xs">-</span>
                  )}
                </td>
                <td className="p-3 text-center">
                  <select
                    value={quote.status}
                    onChange={(e) => updateStatus(quote.id, e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white text-sm rounded px-2 py-1"
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </td>
                <td className="p-3 text-center">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedQuote(quote)} className="text-slate-400 hover:text-white">
                    <Eye className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Quote Detail Modal */}
      {selectedQuote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl bg-slate-800 border-slate-700 max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white">Quote Details</CardTitle>
                <p className="text-slate-400 text-sm mt-1">{selectedQuote.id}</p>
              </div>
              <Button variant="ghost" onClick={() => setSelectedQuote(null)} className="text-slate-400">
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-700/30 rounded-lg">
                <div>
                  <p className="text-slate-400 text-xs">Customer</p>
                  <p className="text-white">{selectedQuote.contact_name}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-xs">Email</p>
                  <p className="text-sky-400">{selectedQuote.contact_email}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-xs">Company</p>
                  <p className="text-white">{selectedQuote.company_name || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-xs">Delivery Port</p>
                  <p className="text-white">{selectedQuote.delivery_port}</p>
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="text-white font-medium mb-2">Requested Items</h4>
                <div className="space-y-2">
                  {selectedQuote.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-slate-700/30 rounded">
                      <div>
                        <p className="text-white text-sm">{item.name}</p>
                        <p className="text-slate-400 text-xs">{item.category}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white">{item.quantity} x ${item.unit_price?.toFixed(2)}</p>
                        <p className="text-sky-400">${item.line_total?.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-3 pt-3 border-t border-slate-600">
                  <span className="text-slate-300">Total</span>
                  <span className="text-sky-400 font-bold text-lg">${selectedQuote.total?.toFixed(2)}</span>
                </div>
              </div>

              {/* Notes */}
              {selectedQuote.notes && (
                <div>
                  <h4 className="text-white font-medium mb-2">Notes</h4>
                  <p className="text-slate-300 bg-slate-700/30 p-3 rounded">{selectedQuote.notes}</p>
                </div>
              )}

              {/* Attachments */}
              {selectedQuote.attachments && selectedQuote.attachments.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-2">Attached Files</h4>
                  <div className="space-y-2">
                    {selectedQuote.attachments.map((url, idx) => {
                      const filename = url.split('/').pop();
                      return (
                        <a
                          key={idx}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-slate-700/30 rounded hover:bg-slate-700/50 transition-colors"
                        >
                          <div className="w-10 h-10 bg-sky-500/20 rounded flex items-center justify-center">
                            <FileText className="w-5 h-5 text-sky-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-white text-sm">{filename}</p>
                            <p className="text-slate-400 text-xs">Click to download</p>
                          </div>
                          <Upload className="w-4 h-4 text-slate-400 rotate-180" />
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Date */}
              <div className="text-slate-400 text-xs text-right">
                Created: {new Date(selectedQuote.created_at).toLocaleString()}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Orders Tab
const OrdersTab = ({ headers }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders`, { headers });
      setOrders(res.data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${id}`, { status }, { headers });
      toast.success('Order updated');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-white">Orders ({orders.length})</h2>
      <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-700/50">
            <tr>
              <th className="text-left p-3 text-slate-300">Order ID</th>
              <th className="text-left p-3 text-slate-300">Title</th>
              <th className="text-left p-3 text-slate-300">Service</th>
              <th className="text-left p-3 text-slate-300">Port</th>
              <th className="text-center p-3 text-slate-300">Priority</th>
              <th className="text-center p-3 text-slate-300">Status</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.id} className="border-t border-slate-700 hover:bg-slate-700/30">
                <td className="p-3 text-white font-mono text-sm">{order.id?.slice(0, 8)}...</td>
                <td className="p-3 text-white">{order.title}</td>
                <td className="p-3 text-slate-400">{order.service_type}</td>
                <td className="p-3 text-slate-400">{order.delivery_port}</td>
                <td className="p-3 text-center">
                  <span className={`px-2 py-1 rounded text-xs ${order.priority === 'urgent' ? 'bg-red-500/20 text-red-400' : 'bg-slate-500/20 text-slate-400'}`}>
                    {order.priority}
                  </span>
                </td>
                <td className="p-3 text-center">
                  <select
                    value={order.status}
                    onChange={(e) => updateStatus(order.id, e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white text-sm rounded px-2 py-1"
                  >
                    <option value="pending">Pending</option>
                    <option value="in_review">In Review</option>
                    <option value="in_production">In Production</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Customers Tab
const CustomersTab = ({ headers }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers });
      setUsers(res.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-white">Customers ({users.length})</h2>
      <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-700/50">
            <tr>
              <th className="text-left p-3 text-slate-300">Company</th>
              <th className="text-left p-3 text-slate-300">Contact Person</th>
              <th className="text-left p-3 text-slate-300">Email</th>
              <th className="text-left p-3 text-slate-300">Phone</th>
              <th className="text-left p-3 text-slate-300">Registered</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-t border-slate-700 hover:bg-slate-700/30">
                <td className="p-3 text-white font-medium">{user.company_name}</td>
                <td className="p-3 text-slate-300">{user.contact_person}</td>
                <td className="p-3 text-sky-400">{user.email}</td>
                <td className="p-3 text-slate-400">{user.phone || '-'}</td>
                <td className="p-3 text-slate-400 text-sm">{new Date(user.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Materials Tab
const MaterialsTab = ({ headers }) => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      const res = await axios.get(`${API}/admin/materials`, { headers });
      setMaterials(res.data);
    } catch (error) {
      toast.error('Failed to load materials');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this material?')) return;
    try {
      await axios.delete(`${API}/admin/materials/${id}`, { headers });
      toast.success('Material deleted');
      fetchMaterials();
    } catch (error) {
      toast.error('Failed to delete material');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Materials ({materials.length})</h2>
        <Button onClick={() => setShowAddModal(true)} className="bg-sky-500 hover:bg-sky-600">
          <Plus className="w-4 h-4 mr-2" /> Add Material
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {materials.map((material) => (
          <Card key={material.id} className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              {material.image_url && (
                <img src={material.image_url} alt={material.name} className="w-full h-32 object-cover rounded mb-3" />
              )}
              <h3 className="text-white font-medium">{material.name}</h3>
              <p className="text-slate-400 text-sm">{material.category}</p>
              <p className="text-slate-500 text-xs">{material.material_type}</p>
              <p className="text-sky-400 mt-2">{material.price_range}</p>
              <div className="flex gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={() => setEditingMaterial(material)} className="border-slate-600 text-slate-300">
                  <Edit className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(material.id)} className="border-red-600 text-red-400 hover:bg-red-500/20">
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {(showAddModal || editingMaterial) && (
        <MaterialModal
          material={editingMaterial}
          headers={headers}
          onClose={() => { setShowAddModal(false); setEditingMaterial(null); }}
          onSave={() => { fetchMaterials(); setShowAddModal(false); setEditingMaterial(null); }}
        />
      )}
    </div>
  );
};

// Material Modal
const MaterialModal = ({ material, headers, onClose, onSave }) => {
  const [formData, setFormData] = useState(material || {
    name: '',
    category: 'pipe',
    material_type: '',
    size: '',
    specifications: {},
    stock_status: 'in_stock',
    price_range: '',
    image_url: ''
  });
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const res = await axios.post(`${API}/admin/upload-image`, formDataUpload, { headers });
      setFormData({ ...formData, image_url: res.data.url });
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (material) {
        await axios.patch(`${API}/admin/materials/${material.id}`, formData, { headers });
        toast.success('Material updated');
      } else {
        await axios.post(`${API}/admin/materials`, formData, { headers });
        toast.success('Material created');
      }
      onSave();
    } catch (error) {
      toast.error('Failed to save material');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">{material ? 'Edit Material' : 'Add New Material'}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-300">Name</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-slate-700 border-slate-600 text-white" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Category</Label>
                <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full bg-slate-700 border-slate-600 text-white rounded-md p-2">
                  <option value="pipe">Pipe</option>
                  <option value="flange">Flange</option>
                  <option value="elbow">Elbow</option>
                  <option value="valve">Valve</option>
                </select>
              </div>
              <div>
                <Label className="text-slate-300">Stock Status</Label>
                <select value={formData.stock_status} onChange={(e) => setFormData({ ...formData, stock_status: e.target.value })} className="w-full bg-slate-700 border-slate-600 text-white rounded-md p-2">
                  <option value="in_stock">In Stock</option>
                  <option value="limited">Limited</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Material Type</Label>
              <Input value={formData.material_type} onChange={(e) => setFormData({ ...formData, material_type: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Size</Label>
              <Input value={formData.size} onChange={(e) => setFormData({ ...formData, size: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Price Range</Label>
              <Input value={formData.price_range} onChange={(e) => setFormData({ ...formData, price_range: e.target.value })} placeholder="e.g., $50-100/m" className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Image</Label>
              <div className="flex gap-2">
                <Input type="file" accept="image/*" onChange={handleImageUpload} className="bg-slate-700 border-slate-600 text-white" />
                {uploading && <span className="text-slate-400">Uploading...</span>}
              </div>
              {formData.image_url && <img src={formData.image_url} alt="Preview" className="mt-2 h-20 rounded" />}
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">Cancel</Button>
              <Button type="submit" className="bg-sky-500 hover:bg-sky-600">Save</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Pricing Tab
const PricingTab = ({ headers }) => {
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/pricing?active_only=false`, { headers });
      setPricing(res.data);
    } catch (error) {
      toast.error('Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  const updatePrice = async (id, unit_price) => {
    try {
      await axios.patch(`${API}/pricing/${id}`, { unit_price: parseFloat(unit_price) }, { headers });
      toast.success('Price updated');
      fetchPricing();
    } catch (error) {
      toast.error('Failed to update price');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-white">Pricing ({pricing.length})</h2>
      <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-700/50">
            <tr>
              <th className="text-left p-3 text-slate-300">Name</th>
              <th className="text-left p-3 text-slate-300">Category</th>
              <th className="text-left p-3 text-slate-300">Unit</th>
              <th className="text-right p-3 text-slate-300">Unit Price</th>
              <th className="text-center p-3 text-slate-300">Active</th>
              <th className="text-center p-3 text-slate-300">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pricing.map((item) => (
              <tr key={item.id} className="border-t border-slate-700 hover:bg-slate-700/30">
                <td className="p-3 text-white">{item.name}</td>
                <td className="p-3 text-slate-400 capitalize">{item.category}</td>
                <td className="p-3 text-slate-400">{item.unit}</td>
                <td className="p-3 text-right">
                  {editingItem === item.id ? (
                    <Input
                      type="number"
                      step="0.01"
                      defaultValue={item.unit_price}
                      onBlur={(e) => { updatePrice(item.id, e.target.value); setEditingItem(null); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { updatePrice(item.id, e.target.value); setEditingItem(null); } }}
                      className="w-24 bg-slate-700 border-slate-600 text-white text-right"
                      autoFocus
                    />
                  ) : (
                    <span className="text-sky-400 font-medium">${item.unit_price?.toFixed(2)}</span>
                  )}
                </td>
                <td className="p-3 text-center">
                  <span className={`px-2 py-1 rounded text-xs ${item.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {item.is_active ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="p-3 text-center">
                  <Button variant="ghost" size="sm" onClick={() => setEditingItem(item.id)} className="text-slate-400 hover:text-white">
                    <Edit className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Contacts Tab
const ContactsTab = ({ headers }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/admin/contacts`, { headers });
      setContacts(res.data);
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/contacts/${id}`, { status }, { headers });
      toast.success('Status updated');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-white">Contact Messages ({contacts.length})</h2>
      <div className="space-y-4">
        {contacts.map((contact) => (
          <Card key={contact.id} className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-white font-medium">{contact.subject}</h3>
                  <p className="text-slate-400 text-sm">{contact.name} • {contact.email}</p>
                  {contact.company && <p className="text-slate-500 text-xs">{contact.company}</p>}
                </div>
                <select
                  value={contact.status}
                  onChange={(e) => updateStatus(contact.id, e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white text-sm rounded px-2 py-1"
                >
                  <option value="new">New</option>
                  <option value="read">Read</option>
                  <option value="replied">Replied</option>
                </select>
              </div>
              <p className="text-slate-300 mt-3 text-sm">{contact.message}</p>
              <p className="text-slate-500 text-xs mt-2">{new Date(contact.created_at).toLocaleString()}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-12">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
  </div>
);

export default TeamAdminDashboard;
